/* eslint-disable */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getLessonById,
  getFragmentProgress,
  startFragment,
  completeFragment,
  finishLesson,
  submitPracticeProgress,
  type LessonFragment,
} from '../../api/lessonsService';
import './CourseLearning.css';
import FragmentContent from "./components/Fragment/FragmentContent";
import {useLessonStore} from "../../store/useLessonStore";
import { getCourseWithProgress } from '../../api/courcesService';

const CourseLearning: React.FC = () => {
  const { t } = useTranslation();
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {currentFragmentIndex, lessonProgressData, lessonData, setLessonData, setLessonProgressData, setCurrentFragment } = useLessonStore()
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fragments, setFragments] = useState<LessonFragment[]>([]);
  const [fragmentStartTime, setFragmentStartTime] = useState<number>(Date.now());
  const [displayedFragments, setDisplayedFragments] = useState<number[]>([]);
  const [completedPractices, setCompletedPractices] = useState<Set<number>>(new Set());
  const [isLessonFinished, setIsLessonFinished] = useState<boolean>(false);
  const [courseWProgress, setCourseWProgress] = useState<any>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchLessonAndProgress = async () => {
      if (!courseId || !lessonId) {
        setError('Course ID and Lesson ID are required');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [lessonResult, fragmentProgressResp, courseWithProgress] = await Promise.all([
          getLessonById(parseInt(lessonId)),
          getFragmentProgress(parseInt(lessonId)),
          getCourseWithProgress(parseInt(courseId)),
        ]);
        setLessonData(lessonResult);
        setCourseWProgress(courseWithProgress);
        setLessonProgressData(Object.values(fragmentProgressResp?.fragmentProgress) || []);

        // Use fragments from API response
        setFragments(lessonResult.fragments || []);

        // If the lesson is finished (from course progress), show all fragments
        const lessonFinished = courseWithProgress?.progressInfo?.finishedLessons?.some((l: any) => l.id === lessonResult.id);
        setIsLessonFinished(Boolean(lessonFinished));

        if (lessonFinished) {
          const total = lessonResult.fragments?.length || 0;
          const lastIndex = Math.max(0, total - 1);
          setCurrentFragment(lastIndex);
          setDisplayedFragments(Array.from({ length: total }, (_, i) => i));
        } else {
          // Check if there's a fragment parameter in the URL
          const fragmentParam = searchParams.get('fragment');
          if (fragmentParam) {
            const fragmentIndex = parseInt(fragmentParam);
            if (!isNaN(fragmentIndex) && fragmentIndex >= 0 && fragmentIndex < (lessonResult.fragments?.length || 0)) {
              setCurrentFragment(fragmentIndex);
              // Show all fragments up to this index
              const fragmentsToShow = Array.from({ length: fragmentIndex + 1 }, (_, i) => i);
              setDisplayedFragments(fragmentsToShow);
            }
          } else {
            // If no fragment parameter, find the appropriate starting fragment based on progress
            const appropriateIndex = findAppropriateFragmentIndex(fragmentProgressResp?.fragmentProgress || []);
            setCurrentFragment(appropriateIndex);
            // Show all fragments up to this index
            const fragmentsToShow = Array.from({ length: appropriateIndex + 1 }, (_, i) => i);
            setDisplayedFragments(fragmentsToShow);
          }
        }

      } catch (err) {
        console.error('Error fetching lesson or progress:', err);
        setError(t('product.learn.errorLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchLessonAndProgress();
  }, [courseId, lessonId, searchParams]);

  // Helper to find the appropriate fragment index based on progress
  const findAppropriateFragmentIndex = (progressData: any[]): number => {
    if (!progressData || progressData.length === 0) {
      return 0; // No progress, start from first fragment
    }

    // Find the last completed fragment
    let lastCompletedIndex = -1;
    for (let i = 0; i < progressData.length; i++) {
      if (progressData[i].completed) {
        lastCompletedIndex = i;
      } else {
        break; // Stop at first incomplete fragment
      }
    }

    // Return the next fragment after the last completed one, or 0 if none completed
    return lastCompletedIndex + 1;
  };

  const handleClose = () => {
    navigate(`/course/${courseId}`);
  };

  function position(elem: any) {
    var left = 0,
      top = 0;

    do {
      left += elem.offsetLeft-elem.scrollLeft;
      top += elem.offsetTop-elem.scrollTop;
    } while ( elem = elem.offsetParent );

    return [ left, top ];
  }

  const handleNext = async () => {
    if (currentFragmentIndex < fragments.length - 1) {
      // Complete current fragment before moving to next
      try {
        if (lessonData && courseId && lessonId) {
          const currentFragment = fragments[currentFragmentIndex];
          const timeSpent = Math.floor((Date.now() - fragmentStartTime) / 1000);

          const {data} = await completeFragment(
            parseInt(courseId),
            lessonData.chapter.id,
            parseInt(lessonId),
            currentFragment.id,
            currentFragmentIndex,
            timeSpent
          );

          setLessonProgressData(Object.values(data?.fragmentProgress) || []);
        }
      } catch (error) {
        console.error('Error completing fragment:', error);
      }

      // Move to next fragment
      const nextFragmentIndex = currentFragmentIndex + 1;
      setCurrentFragment(nextFragmentIndex);
      setFragmentStartTime(Date.now());

      // Add the next fragment to displayed fragments
      setDisplayedFragments(prev => [...prev, nextFragmentIndex]);

      // Start the next fragment
      try {
        if (lessonData && courseId && lessonId) {
          const nextFragment = fragments[nextFragmentIndex];
          await startFragment(
            parseInt(courseId),
              lessonData.chapter.id,
            parseInt(lessonId),
            nextFragment.id,
            nextFragmentIndex
          );
        }
      } catch (error) {
        console.error('Error starting next fragment:', error);
      }



      // Scroll to the new fragment after a short delay to ensure it's rendered
      setTimeout(() => {
        if (contentRef.current) {
          const fragmentElements = contentRef.current.querySelectorAll('.fragment-content');
          if (fragmentElements.length > 0) {
            const lastFragment = fragmentElements[fragmentElements.length - 1];
            const positions = position(lastFragment);
            console.log('positions', positions);
            document.querySelector('.content')?.scrollTo({top: positions[1] - 90, behavior: 'smooth'});
            // lastFragment.scrollIntoView({
            //   behavior: 'smooth',
            //   block: 'start',
            //   inline: 'nearest'
            // });
          }
        }
      }, 100);
    } else {
      // Complete the last fragment and the lesson
      try {
        if (lessonData && courseId && lessonId) {
          const currentFragment = fragments[currentFragmentIndex];
          const timeSpent = Math.floor((Date.now() - fragmentStartTime) / 1000);

          // Complete the last fragment
         const {data} =  await completeFragment(
            parseInt(courseId),
              lessonData.chapter.id,
            parseInt(lessonId),
            currentFragment.id,
            currentFragmentIndex,
            timeSpent
          );
          setLessonProgressData(Object.values(data?.fragmentProgress) || []);


          // Finish the lesson using the new finishLesson function
          await finishLesson(
            parseInt(courseId),
              lessonData.chapter.id,
            parseInt(lessonId)
          );

          navigate(`/course/${courseId}`);
        }
      } catch (error) {
        console.error('Error completing lesson:', error);
        alert(t('product.learn.errorComplete'));
      }
    }
  };

  // Practice handlers
  const handlePracticeStart = (practiceId: number) => {
    console.log('Practice started:', practiceId);
    // TODO: Implement practice start logic
  };

  const handlePracticeHint = (practiceId: number) => {
    console.log('Practice hint requested:', practiceId);
    // TODO: Implement practice hint logic
  };

  const handlePracticeSubmit = (practiceId: number, selectedOption: string) => {
    console.log('Practice submitted:', practiceId, selectedOption);
    // Mark practice as completed for single choice tests
    markPracticeAsCompleted(practiceId);
  };

  const handlePracticeCompleted = async (practiceId: number) => {
    console.log('Practice completed:', practiceId);
    // Mark practice as completed for prompt creation practices
    markPracticeAsCompleted(practiceId);
    const result = await getFragmentProgress(parseInt(lessonId || ''))
    setLessonProgressData(Object.values(result?.fragmentProgress))
  };

  // Check if all required practices for the current fragment are completed
  const areAllRequiredPracticesCompleted = (): boolean => {
    const currentFragment = fragments[currentFragmentIndex];
    if (!currentFragment || !currentFragment.practices) {
      return true; // No practices, so all are "completed"
    }

    const requiredPractices = currentFragment.practices.filter(practice => practice.isRequired);
    if (requiredPractices.length === 0) {
      return true; // No required practices, so all are "completed"
    }

    // Check if all required practices are completed
    return requiredPractices.every(practice => completedPractices.has(practice.id));
  };

  // Mark a practice as completed
  const markPracticeAsCompleted = (practiceId: number) => {
    setCompletedPractices(prev => new Set(Array.from(prev).concat(practiceId)));
  };

  // Check if a practice is completed based on fragment progress data
  const isPracticeCompleted = (practiceId: number): boolean => {
    const currentFragment = fragments[currentFragmentIndex];
    if (!currentFragment) return false;

    // Check if the current fragment has progress data
    const fragmentProgress = lessonProgressData.find((fp:any) => fp.fragmentId === currentFragment.id);
    // console.log('check practice with id ', currentFragment, fragmentProgressData);
    if (!fragmentProgress || !fragmentProgress.practiceResults) return false;
    // Check if this practice exists in the practice results
    return fragmentProgress.practiceResults.hasOwnProperty(practiceId.toString());
  };

  // Handle skipping all practices in current fragment
  const handleSkipPractice = async () => {
    const currentFragment = fragments[currentFragmentIndex];
    if (!currentFragment || !currentFragment.practices) {
      return;
    }

    try {
      // Submit all practices as skipped
      for (const practice of currentFragment.practices) {
          await submitPracticeProgress({
            courseId: courseId || '',
            fragmentIndex: currentFragmentIndex,
            fragmentId: currentFragment.id,
            practiceId: practice.id,
            lessonId: lessonId || 0,
            practiceType: practice.practiceType,
            answer: 'SKIPPED',
            isCorrect: false
          });
      }

      const result = await getFragmentProgress(parseInt(lessonId || ''))
      setLessonProgressData(Object.values(result?.fragmentProgress))

      // Proceed to next fragment after skipping
      handleNext();
    } catch (error) {
      console.error('Error skipping practices:', error);
    }
  };

  // Check if current fragment has required practices
  const hasRequiredPractices = (): boolean => {
    const currentFragment = fragments[currentFragmentIndex];
    if (!currentFragment || !currentFragment.practices) {
      return false;
    }
    return currentFragment.practices.some(practice => practice.isRequired);
  };

  if (loading) {
    return (
      <div className="course-learning-container">
        <div className="course-learning-loading">{t('product.learn.loading')}</div>
      </div>
    );
  }

  if (error || !lessonData) {
    return (
      <div className="course-learning-container">
        <div className="course-learning-error">
          {error || t('product.learn.notFound')}
          <button onClick={() => navigate('/courses')}>{t('product.learn.goBack')}</button>
        </div>
      </div>
    );
  }

  // Calculate progress percentage
  const completedFragments = lessonProgressData?.filter((fp:any) => fp.lessonId == lessonId);
  let fragmentProgress = fragments.length > 0 ? (completedFragments.length / fragments.length) * 100 : 0;
  if(isLessonFinished){
    fragmentProgress = 100;
  }

  return (
    <div className="course-learning-container">
      <header className="course-learning-header">
        <button className="close-btn" onClick={handleClose} aria-label={t('product.learn.closeAria')}>✕</button>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: `${fragmentProgress}%` }}></div>
        </div>
      </header>

      <div className="course-learning-content">
        <div className="lesson-content" ref={contentRef}>
          {displayedFragments.map((fragmentIndex, index) => {
            const fragment = fragments[fragmentIndex];
            const practices = fragment?.practices || [];

            return (
              <div key={fragmentIndex} className="fragment-content">
                <h1 className="lesson-main-title">{fragment.title}</h1>
              <div className="lesson-text-content">
                <FragmentContent
                  content={fragment?.content || lessonData.content || ''}
                  practices={practices}
                  currentFragmentIndex={fragmentIndex}
                  courseId={courseId || ''}
                  currentFragmentId={fragment.id}
                  lessonId={lessonId || ''}
                  onPracticeStart={handlePracticeStart}
                  onPracticeHint={handlePracticeHint}
                  onPracticeSubmit={handlePracticeSubmit}
                  onPracticeCompleted={handlePracticeCompleted}
                  onMoveNext={handleNext}
                  // isPracticeCompleted={isPracticeCompleted}
                />
              </div>
            </div>
            );
          })}
        </div>
      </div>
      {!isLessonFinished && (
        <div className="course-learning-navigation">
          {hasRequiredPractices() && !areAllRequiredPracticesCompleted() ? (
            <button
              className="nav-btn skip-btn"
              onClick={handleSkipPractice}
              aria-label={t('product.learn.skipAria')}
            >
              {t('product.learn.skipPractice')}
            </button>
          ) : (
            <button
              className="nav-btn continue-btn"
              onClick={handleNext}
            >
              {currentFragmentIndex === fragments.length - 1 ? t('product.learn.finishLesson') : t('product.learn.continue')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseLearning;
